import { Link } from "react-router-dom";
import { Instagram } from "lucide-react";
import { NAV, SITE, LOGO } from "@/data/content";
import { CoveButton } from "@/components/CoveButton";
import { Reveal } from "@/components/motion";

const LEGAL = [
  { label: "Privacy Policy", to: "/privacy" },
  { label: "Terms & Conditions", to: "/terms" },
  { label: "Accessibility", to: "/accessibility" },
];

export const Footer = () => {
  return (
    <footer className="relative bg-cove-petal text-cove-slate" data-testid="site-footer">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12 pt-20 md:pt-28 pb-10">
        <Reveal>
          <div className="flex flex-col items-center text-center pb-14">
            <img
              src={LOGO}
              alt="Cranio Cove logo"
              width="420"
              height="500"
              className="w-72 md:w-[26rem] object-contain"
            />
            <p className="mt-6 max-w-xl text-cove-olive leading-relaxed">
              A private craniosacral practice offering gentle, individualized sessions in a
              peaceful and supportive environment.
            </p>
            <div className="mt-8">
              <CoveButton calendly variant="primary" testId="footer-book-button">
                Book a Session
              </CoveButton>
            </div>
            <p className="mt-6 text-xs text-cove-taupe">
              Payment accepted: {SITE.payments.join(" · ")}
            </p>
          </div>
        </Reveal>

        <div className="grid gap-14 md:grid-cols-2 pt-10 border-t border-cove-slate/10">
          <Reveal delay={0.1}>
            <nav aria-label="Footer" className="text-sm">
              <h2 className="font-serif text-xl mb-5 text-cove-slate">Explore</h2>
              <ul className="space-y-3">
                {NAV.map((item) => (
                  <li key={item.to}>
                    <Link
                      to={item.to}
                      className="text-cove-olive hover:text-cove-rose transition-colors duration-300"
                      data-testid={`footer-link-${item.to.replace(/\//g, "") || "home"}`}
                    >
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          </Reveal>

          <Reveal delay={0.2}>
            <div className="text-sm">
              <h2 className="font-serif text-xl mb-5 text-cove-slate">Get in touch</h2>
              <ul className="space-y-3 text-cove-olive">
                <li>
                  <a href={`mailto:${SITE.email}`} className="hover:text-cove-rose transition-colors duration-300">
                    {SITE.email}
                  </a>
                </li>
                <li>
                  <a href={`tel:${SITE.phoneHref}`} className="hover:text-cove-rose transition-colors">{SITE.phone}</a>
                </li>
                <li>Serving {SITE.serviceArea}</li>
                <li>
                  <a href={SITE.googleReviewUrl} target="_blank" rel="noopener noreferrer" className="hover:text-cove-rose transition-colors" data-testid="footer-google-review">
                    Leave a Google review
                  </a>
                </li>
              </ul>
              <div className="mt-6 flex items-center gap-4">
                <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="text-cove-olive hover:text-cove-rose transition-colors">
                  <Instagram size={20} />
                </a>
              </div>
            </div>
          </Reveal>
        </div>

        <div className="mt-16 pt-8 border-t border-cove-slate/15">
          <p className="text-xs leading-relaxed text-cove-taupe max-w-3xl">
            <strong className="font-medium text-cove-olive">Health disclaimer:</strong>{" "}
            Craniosacral therapy is a complementary practice intended to support relaxation and
            general wellbeing. It is not a substitute for medical care and does not diagnose,
            treat, or cure any medical condition. Please consult a qualified healthcare provider
            for medical concerns.
          </p>
          <div className="mt-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <p className="text-xs text-cove-taupe">
              © {new Date().getFullYear()} Cranio Cove. All rights reserved.
            </p>
            <ul className="flex flex-wrap items-center gap-6 text-xs">
              {LEGAL.map((item) => (
                <li key={item.to}>
                  <Link to={item.to} className="text-cove-taupe hover:text-cove-rose transition-colors" data-testid={`footer-legal-${item.to.replace(/\//g, "")}`}>
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
